import React, { FunctionComponent, useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRadio,
  IonRadioGroup,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { add, camera, close, trash } from 'ionicons/icons';
import displayPlan from '../../../icons/display-plan.svg';
import { AnnotationModel, AnnotationType } from '../models/annotation.model';
import { useTranslation } from 'react-i18next';
import { AnnotationUpdatedFields } from '../types/annotation.types';
import { EditActionButton } from '../../shared/components/edit-action-button.component';
import { useAnnotationForm } from '../hooks/use-annotation-form.hook';
import { useHistory } from 'react-router-dom';
import { useMappedArea } from '../hooks/use-mapped-area.hook';
import { useAnnotationAutocomplete } from '../../annotation/hooks';
import { useMetaLibrary } from '../hooks/use-meta-library.hook';
import { SystemsModal } from './systems-modal.component';
import { ConstituentsModal } from './constituents-modal.component';
import { CharacteristicsModal } from './characteristics-modal.component';
import { MetaLibrarySubSystemModel, MetaLibraryConstituentModel } from '../models/meta-library.model';
import './annotation-form.component.scss';

interface AnnotationFormProps {
  isPlanExported: boolean;
  annotationToVisualize: AnnotationModel | null;
  createAnnotation: (annotation: {
    type: AnnotationType;
    title: string;
    content?: string;
    pictureBinary?: string;
    status?: string;
    equipmentCode?: string;
  }) => void;
  updateAnnotation: (id: string, updatedAnnotationFields: AnnotationUpdatedFields) => void;
  deleteAnnotation: (id: string, fileName: string | null) => void;
  closePopover: () => void;
  annotationPositionName: string;
  showDisplayPlanIcon?: boolean;
  roomId?: string;
  onAnnotationIdPass?: (annotationId: string) => void;
  clickedElementType?: string;
}

export const AnnotationForm: FunctionComponent<AnnotationFormProps> = ({
                                                                         isPlanExported,
                                                                         updateAnnotation,
                                                                         annotationToVisualize,
                                                                         deleteAnnotation,
                                                                         createAnnotation,
                                                                         closePopover,
                                                                         annotationPositionName,
                                                                         showDisplayPlanIcon = false,
                                                                         roomId,
                                                                         onAnnotationIdPass,
                                                                         clickedElementType = '',
                                                                       }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { getAssetAncestors } = useMappedArea(() => Promise.resolve(null as any));

  const titleRef = useRef<HTMLIonInputElement | null>(null);
  const titleContainerRef = useRef<HTMLDivElement | null>(null);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isSystemsModalOpen, setIsSystemsModalOpen] = useState(false);
  const [isConstituentsModalOpen, setIsConstituentsModalOpen] = useState(false);
  const [isCharacteristicsModalOpen, setIsCharacteristicsModalOpen] = useState(false);
  const [selectedSubSystem, setSelectedSubSystem] = useState<MetaLibrarySubSystemModel | null>(null);
  const [selectedConstituent, setSelectedConstituent] = useState<MetaLibraryConstituentModel | null>(null);
  const [selectedSystemCode, setSelectedSystemCode] = useState<string>('');
  const [selectedConstituentCode, setSelectedConstituentCode] = useState<string>('');
  const [selectedLineCode, setSelectedLineCode] = useState<string>('');
  const [selectedColumnCode, setSelectedColumnCode] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsPosition, setSuggestionsPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const {
    annotationFormFields,
    setAnnotationFormFields,
    onPictureInputValueChanged,
    onCreateButtonClicked,
    onUpdateButtonClicked,
    onDeleteButtonClicked,
  } = useAnnotationForm(annotationToVisualize, createAnnotation, updateAnnotation, deleteAnnotation, titleRef);

  const { 
    suggestions, 
    getSuggestions, 
    loadAnnotationById, 
    clearSuggestions,
    isLoadingAnnotation 
  } = useAnnotationAutocomplete();

  const { systems, isLoading: isLoadingMetaLibrary } = useMetaLibrary();

  // Effet pour reconstruire les informations d'équipement depuis le code en mode modification
  useEffect(() => {
    if (annotationToVisualize && annotationToVisualize.equipmentCode && systems.length > 0) {
      const code = annotationToVisualize.equipmentCode;
      
      // Parser le code : systemCode(1) + subSystemCode(1) + constituentCode(2) + lineCode(1) + columnCode(1)
      if (code.length >= 6) {
        const systemCode = code.charAt(0);
        const subSystemCode = code.charAt(1);
        const constituentCode = code.substring(2, 4);
        const lineCode = code.charAt(4);
        const columnCode = code.charAt(5);
        
        // Chercher le système
        const system = systems.find(s => s.code === systemCode);
        if (system) {
          // Chercher le sous-système
          const subSystem = system.metaLibrarySubSystems.find(ss => ss.code === subSystemCode);
          if (subSystem) {
            // Chercher le constituant
            const constituent = subSystem.metaLibraryConstituents.find(c => c.code === constituentCode);
            if (constituent) {
              // Initialiser les états pour afficher la card
              setSelectedSystemCode(systemCode);
              setSelectedSubSystem(subSystem);
              setSelectedConstituent(constituent);
              setSelectedLineCode(lineCode);
              setSelectedColumnCode(columnCode);
            }
          }
        }
      }
    }
  }, [annotationToVisualize, systems]);

  const previewUrl = annotationFormFields.pictureBinary
    ? `data:image/*;base64,${ annotationFormFields.pictureBinary }`
    : null;
  const isCreationMode = annotationToVisualize === null;
  const isCreateButtonDisabled = !annotationFormFields.type || !annotationFormFields.title;
  const titleLabel = annotationToVisualize
    ? t('plan.annotationForm.consultationTitle')
    : t('plan.annotationForm.creationTitle');
  const onDeletePictureButtonClicked = () => setAnnotationFormFields({ ...annotationFormFields, pictureBinary: null });

  const handleTitleInput = (value: string) => {
    setAnnotationFormFields((prev) => ({ ...prev, title: value }));
    
    // Afficher les suggestions uniquement en mode création
    if (isCreationMode && value.length >= 2) {
      getSuggestions(value);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      clearSuggestions(); // Effacer les suggestions quand < 2 caractères
    }
  };

  // Synchroniser showSuggestions avec les suggestions reçues
  React.useEffect(() => {
    const shouldShow = isCreationMode && suggestions.length > 0 && annotationFormFields.title && annotationFormFields.title.length >= 2;
    if (shouldShow && !showSuggestions) {
      setShowSuggestions(true);
    }
  }, [suggestions, isCreationMode, annotationFormFields.title, showSuggestions]);

  const handleSuggestionSelect = async (annotationId: string) => {
    const annotation = await loadAnnotationById(annotationId);
    
    if (annotation) {
      setAnnotationFormFields({
        type: annotation.type,
        title: annotation.title,
        content: annotation.content,
        status: annotation.status,
      });
    }
    
    setShowSuggestions(false);
    clearSuggestions();
  };

  const handleTitleBlur = () => {
    // Ne rien faire - les suggestions restent visibles
    // Elles seront fermées uniquement lors de la sélection ou quand l'utilisateur efface le texte
  };

  const handleTitleFocus = () => {
    // Calculer la position des suggestions
    updateSuggestionsPosition();
    
    // Réafficher les suggestions si on a déjà du texte
    if (isCreationMode && annotationFormFields.title && annotationFormFields.title.length >= 2) {
      getSuggestions(annotationFormFields.title);
      setShowSuggestions(true);
    }
  };

  const updateSuggestionsPosition = () => {
    if (titleContainerRef.current) {
      const rect = titleContainerRef.current.getBoundingClientRect();
      setSuggestionsPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Mettre à jour la position quand les suggestions apparaissent
  useEffect(() => {
    if (showSuggestions && suggestions.length > 0) {
      updateSuggestionsPosition();
    }
  }, [showSuggestions, suggestions]);

  // Fermer les suggestions si on clique ailleurs
  useEffect(() => {
    if (!showSuggestions) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Vérifier si le clic est en dehors du champ titre et des suggestions
      if (
        titleContainerRef.current && 
        !titleContainerRef.current.contains(target) &&
        !target.closest('.annotation-suggestions')
      ) {
        setShowSuggestions(false);
        clearSuggestions();
      }
    };

    // Ajouter le listener avec un petit délai pour éviter la fermeture immédiate
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions, clearSuggestions]);

  const handleDisplayPlanClick = async () => {
    if (annotationToVisualize && onAnnotationIdPass) {
      onAnnotationIdPass(annotationToVisualize.id);
    }
    
    if (roomId) {
      try {
        const assetAncestors = await getAssetAncestors(roomId);
        
        const hasFloor = assetAncestors.some(ancestor => ancestor.assetType === 'FLOOR');
        
        if (hasFloor) {
          const siteId = assetAncestors.find(ancestor => ancestor.assetType === 'BUILDING_AREA')?.assetId;
          const buildingId = assetAncestors.find(ancestor => ancestor.assetType === 'BUILDING')?.assetId;
          const floorId = assetAncestors.find(ancestor => ancestor.assetType === 'FLOOR')?.assetId;
          
          if (siteId && buildingId && floorId) {
            closePopover();
            history.push(`/sites/${siteId}/buildings/${buildingId}/floors/${floorId}`);
          }
        } else {
          const siteId = assetAncestors.find(ancestor => ancestor.assetType === 'BUILDING_AREA')?.assetId;
          if (siteId) {
            closePopover();
            history.push(`/sites/${siteId}/outdoor-areas`);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des ancêtres:', error);
      }
    }
  };

  const handleSubSystemSelect = (subSystem: MetaLibrarySubSystemModel, systemCode: string): void => {
    setSelectedSubSystem(subSystem);
    setSelectedSystemCode(systemCode);
    setIsSystemsModalOpen(false);
    setIsConstituentsModalOpen(true);
  };

  const handleConstituentsModalClose = () => {
    setIsConstituentsModalOpen(false);
    setSelectedSubSystem(null);
    setSelectedSystemCode('');
  };

  const handleBackToSystems = () => {
    setIsConstituentsModalOpen(false);
    setSelectedSubSystem(null);
    setSelectedSystemCode('');
    setIsSystemsModalOpen(true);
  };

  const handleConstituentSelect = (constituent: MetaLibraryConstituentModel, constituentCode: string): void => {
    setSelectedConstituent(constituent);
    setSelectedConstituentCode(constituentCode);
    setIsConstituentsModalOpen(false);
    setIsCharacteristicsModalOpen(true);
  };

  const handleBackToConstituents = () => {
    setIsCharacteristicsModalOpen(false);
    setSelectedConstituent(null);
    setSelectedConstituentCode('');
    setIsConstituentsModalOpen(true);
  };

  const handleCharacteristicsModalClose = () => {
    setIsCharacteristicsModalOpen(false);
    setSelectedConstituent(null);
    setSelectedConstituentCode('');
    // Réinitialiser aussi les états des constituants lors de la fermeture complète
    setIsConstituentsModalOpen(false);
    setSelectedSubSystem(null);
    setSelectedSystemCode('');
  };

  const handleCharacteristicsValidate = (lineCode: string, columnCode: string) => {
    // Construire le code complet : systemCode + subSystemCode + constituentCode + lineCode + columnCode
    const fullEquipmentCode = `${selectedSystemCode}${selectedSubSystem?.code}${selectedConstituent?.code}${lineCode}${columnCode}`;
    
    // Sauvegarder le code et les codes de ligne/colonne
    setAnnotationFormFields({
      ...annotationFormFields,
      equipmentCode: fullEquipmentCode
    });
    
    setSelectedLineCode(lineCode);
    setSelectedColumnCode(columnCode);
    
    // Fermer toutes les modales
    setIsCharacteristicsModalOpen(false);
    setIsConstituentsModalOpen(false);
    setIsSystemsModalOpen(false);
  };

  const handleDeleteEquipmentCode = () => {
    setAnnotationFormFields({
      ...annotationFormFields,
      equipmentCode: null
    });
    setSelectedSubSystem(null);
    setSelectedConstituent(null);
    setSelectedSystemCode('');
    setSelectedConstituentCode('');
    setSelectedLineCode('');
    setSelectedColumnCode('');
  };

  // Reconstruire les informations d'affichage à partir du state
  const getEquipmentDisplayInfo = () => {
    if (!annotationFormFields.equipmentCode || !selectedConstituent || !selectedSubSystem) {
      return null;
    }

    const characteristic = selectedConstituent.metaLibraryCharacteristics.find(
      char => char.code === `${selectedLineCode}${selectedColumnCode}`
    );

    return {
      code: `${selectedSystemCode}${selectedSubSystem.code}${selectedConstituent.code}`,
      constituentLabel: selectedConstituent.constituent,
      lineValue: characteristic?.lineValue || '',
      columnValue: characteristic?.columnValue || ''
    };
  };

  const equipmentDisplayInfo = getEquipmentDisplayInfo();

  // Vérifier si le bouton d'équipement doit être visible
  const shouldShowEquipmentButton = 
    // Si l'annotation a déjà un equipmentCode, on peut toujours l'afficher/modifier
    (annotationFormFields.equipmentCode !== null && annotationFormFields.equipmentCode !== undefined) ||
    // Sinon, vérifier les conditions basées sur le type d'élément cliqué
    (clickedElementType === 'Pie' && annotationFormFields.type === 'CREATION') ||
    (clickedElementType === 'Equ' && annotationFormFields.type === 'MODIFICATION');

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{ titleLabel }</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={ closePopover } color="dark" fill="clear" size="small">
              <IonIcon slot="icon-only" icon={ close }/>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">

        <IonSelect
          value={ annotationFormFields.status ?? 'NEW' }
          disabled={isPlanExported}
          onIonChange={ (event) =>
            event.detail.value &&
            setAnnotationFormFields({
              ...annotationFormFields,
              status: event.detail.value,
            })
          }
          cancelText={ t('plan.annotationForm.form.status.CANCEL_BUTTON') }
          okText={ t('plan.annotationForm.form.status.SAVE_BUTTON') }
        >
          <IonSelectOption value={ 'NEW' }>
            { t('plan.annotationForm.form.status.NEW') }
          </IonSelectOption>
          <IonSelectOption value={ 'PENDING' }>
            { t('plan.annotationForm.form.status.PENDING') }
          </IonSelectOption>
          <IonSelectOption value={ 'TODO' }>
            { t('plan.annotationForm.form.status.TODO') }
          </IonSelectOption>
          <IonSelectOption value={ 'IN_PROGRESS' }>
            { t('plan.annotationForm.form.status.IN_PROGRESS') }
          </IonSelectOption>
          <IonSelectOption value={ 'DONE' }>
            { t('plan.annotationForm.form.status.DONE') }
          </IonSelectOption>
          <IonSelectOption value={ 'ARCHIVED' }>
            { t('plan.annotationForm.form.status.ARCHIVED') }
          </IonSelectOption>
        </IonSelect>

        <IonList>
          <IonItem lines={ 'none' }>
            <div style={{ width: '100%' }}>
              <IonLabel position={ 'stacked' } style={{ marginBottom: '8px' }}>
                { t('plan.annotationForm.concerningName') }
              </IonLabel>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                width: '100%',
                marginBottom: '16px'
              }}>
                <IonLabel style={{ 
                  fontSize: 12, 
                  display: 'block', 
                  whiteSpace: 'normal',
                  flex: 1,
                  marginRight: '8px'
                }}>
                  { annotationPositionName }
                </IonLabel>
                {showDisplayPlanIcon && (
                  <IonButton 
                    fill="clear" 
                    size="small" 
                    style={{ minWidth: 'auto', margin: 0 }}
                    onClick={handleDisplayPlanClick}
                  >
                    <IonIcon icon={displayPlan} style={{ fontSize: '20px' }} />
                  </IonButton>
                )}
              </div>
            </div>
          </IonItem>

          <IonItem lines="none">
            <div style={{ 
              width: '100%',
              borderBottom: '1px solid var(--ion-color-step-150, #d7d8da)',
              paddingBottom: '10px'
            }}>
              <IonLabel position="stacked">
                { t('plan.annotationForm.form.type.label') }
              </IonLabel>
              <IonButton
                expand="block"
                fill="clear"
                onClick={() => setIsTypeModalOpen(true)}
                disabled={isPlanExported}
                style={{ 
                  marginTop: '8px', 
                  textTransform: 'none',
                  '--padding-start': '0',
                  '--padding-end': '0',
                  '--button-text-align': 'left',
                  height: 'auto',
                  minHeight: '44px'
                } as any}
              >
                <span style={{ 
                  width: '100%', 
                  textAlign: 'left', 
                  display: 'block',
                  color: annotationFormFields.type ? 'var(--ion-text-color, #000)' : 'var(--ion-color-medium, #92949c)',
                  fontSize: '16px',
                  fontWeight: '400'
                }}>
                  { annotationFormFields.type 
                    ? t(`plan.annotationForm.form.type.${annotationFormFields.type.toLowerCase()}`)
                    : t('plan.annotationForm.form.type.placeholder')
                  }
                </span>
              </IonButton>
            </div>
          </IonItem>

          <IonItem lines={showSuggestions && suggestions.length > 0 ? 'none' : 'full'}>
            <div ref={titleContainerRef} style={{ width: '100%', position: 'relative' }}>
              <IonInput
                label={t('plan.annotationForm.form.title.label')}
                disabled={isPlanExported}
                labelPlacement='stacked'
                ref={ titleRef }
                placeholder={ t('plan.annotationForm.form.title.placeholder') }
                onIonInput={ (event) => {
                  const newValue = event.detail.value;
                  if (typeof newValue === 'string') {
                    handleTitleInput(newValue);
                  }
                }}
                onIonBlur={ handleTitleBlur }
                onIonFocus={ handleTitleFocus }
                value={ annotationFormFields.title }
              />
              {isLoadingAnnotation && (
                <div style={{ 
                  position: 'absolute', 
                  top: '100%', 
                  left: 0, 
                  right: 0, 
                  padding: '8px',
                  textAlign: 'center',
                  fontSize: '12px',
                  color: 'var(--ion-color-medium)'
                }}>
                  {t('plan.annotationForm.loadingAnnotation', 'Chargement...')}
                </div>
              )}
            </div>
          </IonItem>
          
          {/* Suggestions en portal pour éviter qu'elles soient cachées */}
          {showSuggestions && suggestions.length > 0 && isCreationMode && suggestionsPosition && ReactDOM.createPortal(
            <div 
              className="annotation-suggestions" 
              style={{
                position: 'fixed',
                top: `${suggestionsPosition.top}px`,
                left: `${suggestionsPosition.left}px`,
                width: `${suggestionsPosition.width}px`,
              }}
            >
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id}
                  className="annotation-suggestion-item"
                  onClick={() => handleSuggestionSelect(suggestion.id)}
                >
                  <div className="suggestion-title">{suggestion.title}</div>
                  {suggestion.content && (
                    <div className="suggestion-content">{suggestion.content}</div>
                  )}
                </div>
              ))}
            </div>,
            document.body
          )}

          {/* Bouton d'ajout visible selon conditions */}
          {shouldShowEquipmentButton && !equipmentDisplayInfo && (
            <IonItem lines="none">
              <IonButton
                expand="block"
                fill="outline"
                onClick={() => setIsSystemsModalOpen(true)}
                disabled={isPlanExported}
                style={{ 
                  width: '100%',
                  marginTop: '8px',
                  marginBottom: '8px'
                }}
              >
                Sélectionner le type d'équipement
              </IonButton>
            </IonItem>
          )}

          {/* Card d'équipement - Visible si un code existe et conditions respectées */}
          {shouldShowEquipmentButton && equipmentDisplayInfo && (
            <IonItem lines="none" style={{ marginTop: '8px', marginBottom: '8px' }}>
              <div style={{
                width: '100%',
                backgroundColor: '#FFFFFF',
                borderRadius: '6px',
                padding: '12px',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#4B4D56',
                    marginBottom: '4px'
                  }}>
                    {equipmentDisplayInfo.code} - {equipmentDisplayInfo.constituentLabel}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#888',
                    lineHeight: '1.4'
                  }}>
                    {equipmentDisplayInfo.lineValue} - {equipmentDisplayInfo.columnValue}
                  </div>
                </div>
                {/* Bouton corbeille toujours visible si card affichée */}
                <IonButton
                  fill="clear"
                  onClick={handleDeleteEquipmentCode}
                  disabled={isPlanExported}
                  style={{
                    '--padding-start': '8px',
                    '--padding-end': '8px',
                    margin: 0
                  }}
                >
                  <IonIcon icon={trash} style={{ fontSize: '20px', color: '#E93A1C' }} />
                </IonButton>
              </div>
            </IonItem>
          )}

          <IonItem>
            <IonTextarea
              label={t('plan.annotationForm.form.comment.label')}
              disabled={isPlanExported}
              labelPlacement='stacked'
              rows={ 6 }
              placeholder={ t('plan.annotationForm.form.comment.placeholder') }
              onIonInput={ (event) => {
                const newValue = event.detail.value;
                if (typeof newValue === 'string') {
                  setAnnotationFormFields((prev) => ({
                    ...prev,
                    content: newValue
                  }));
                }
              }}
              onIonChange={ (event) => {
                const newValue = event.detail.value;
                if (typeof newValue === 'string') {
                  setAnnotationFormFields((prev) => ({
                    ...prev,
                    content: newValue
                  }));
                }
              }}
              value={ annotationFormFields.content }
            />
          </IonItem>

          <IonItem lines={ 'none' }>
            { previewUrl ? (
              <div className={ 'ion-padding-vertical' }>
                <img src={ previewUrl } alt=""/>
                <IonButton onClick={ onDeletePictureButtonClicked } color="tertiary">
                  <IonIcon slot="start" icon={ trash }/>
                  { t('plan.annotationForm.deletePictureButton') }
                </IonButton>
              </div>
            ) : (
              <label>
                <IonButton color={ 'tertiary' } fill={ 'clear' } style={ { pointerEvents: 'none' } } disabled={isPlanExported}>
                  <IonIcon slot={ 'start' } icon={ camera }/>
                  { t('plan.annotationForm.addPictureButton') }
                </IonButton>
                <input onChange={ onPictureInputValueChanged } type="file" accept="image/*"
                       style={ { display: 'none' } }/>
              </label>
            ) }
          </IonItem>
        </IonList>
      </IonContent>
      <IonFooter className={ 'ion-no-border' }>
        <IonToolbar>
          <IonButtons>
            { isCreationMode ? (
              <EditActionButton
                onClick={ onCreateButtonClicked }
                icon={ add }
                disabled={ isCreateButtonDisabled }
                label={ t('plan.annotationForm.createButton') }
                color={ 'secondary' }
              />
            ) : (
              <>
                <EditActionButton
                  onClick={ onDeleteButtonClicked }
                  icon={ trash }
                  label={ t('plan.annotationForm.deleteButton') }
                  color={ 'light' }
                />
                <EditActionButton
                  onClick={ onUpdateButtonClicked }
                  icon={ add }
                  disabled={ isCreateButtonDisabled }
                  label={ t('plan.annotationForm.modifyButton') }
                  color={ 'secondary' }
                />
              </>
            ) }
          </IonButtons>
        </IonToolbar>
      </IonFooter>

      <IonModal isOpen={isTypeModalOpen} onDidDismiss={() => setIsTypeModalOpen(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{ t('plan.annotationForm.form.type.label') }</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setIsTypeModalOpen(false)}>
                <IonIcon icon={close} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonRadioGroup
            value={ annotationFormFields.type }
            onIonChange={ (event) => {
              if (event.detail.value) {
                setAnnotationFormFields({
                  ...annotationFormFields,
                  type: event.detail.value as AnnotationType,
                });
                setIsTypeModalOpen(false);
              }
            }}
          >
            <IonItem 
              button 
              onClick={() => {
                setAnnotationFormFields({
                  ...annotationFormFields,
                  type: "MODIFICATION",
                });
                setIsTypeModalOpen(false);
              }}
            >
              <IonRadio value="MODIFICATION" slot="start" />
              <IonLabel className="ion-text-wrap">
                <h3>{ t('plan.annotationForm.form.type.modification') }</h3>
                <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)', marginTop: '4px' }}>
                  { t('plan.annotationForm.form.type.modificationDescription') }{' '}
                  <span style={{ fontStyle: 'italic' }}>
                    { t('plan.annotationForm.form.type.modificationDescriptionNote') }
                  </span>
                </p>
              </IonLabel>
            </IonItem>

            <IonItem 
              button 
              onClick={() => {
                setAnnotationFormFields({
                  ...annotationFormFields,
                  type: "CREATION",
                });
                setIsTypeModalOpen(false);
              }}
            >
              <IonRadio value="CREATION" slot="start" />
              <IonLabel className="ion-text-wrap">
                <h3>{ t('plan.annotationForm.form.type.creation') }</h3>
                <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)', marginTop: '4px' }}>
                  { t('plan.annotationForm.form.type.creationDescription') }
                </p>
              </IonLabel>
            </IonItem>

            <IonItem 
              button 
              onClick={() => {
                setAnnotationFormFields({
                  ...annotationFormFields,
                  type: "DELETION",
                });
                setIsTypeModalOpen(false);
              }}
            >
              <IonRadio value="DELETION" slot="start" />
              <IonLabel className="ion-text-wrap">
                <h3>{ t('plan.annotationForm.form.type.deletion') }</h3>
                <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)', marginTop: '4px' }}>
                  { t('plan.annotationForm.form.type.deletionDescription') }
                </p>
              </IonLabel>
            </IonItem>

            <IonItem 
              button 
              onClick={() => {
                setAnnotationFormFields({
                  ...annotationFormFields,
                  type: "INFO",
                });
                setIsTypeModalOpen(false);
              }}
            >
              <IonRadio value="INFO" slot="start" />
              <IonLabel className="ion-text-wrap">
                <h3>{ t('plan.annotationForm.form.type.info') }</h3>
                <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)', marginTop: '4px' }}>
                  { t('plan.annotationForm.form.type.infoDescription') }
                </p>
              </IonLabel>
            </IonItem>
          </IonRadioGroup>
        </IonContent>
      </IonModal>

      <SystemsModal
        isOpen={isSystemsModalOpen}
        systems={systems}
        isLoading={isLoadingMetaLibrary}
        onClose={() => setIsSystemsModalOpen(false)}
        onSubSystemSelect={handleSubSystemSelect}
      />

      <ConstituentsModal
        isOpen={isConstituentsModalOpen}
        subSystem={selectedSubSystem}
        systemCode={selectedSystemCode}
        onClose={handleConstituentsModalClose}
        onBack={handleBackToSystems}
        onConstituentSelect={handleConstituentSelect}
      />

      <CharacteristicsModal
        isOpen={isCharacteristicsModalOpen}
        constituent={selectedConstituent}
        constituentCode={selectedConstituentCode}
        onClose={handleCharacteristicsModalClose}
        onBack={handleBackToConstituents}
        onValidate={handleCharacteristicsValidate}
      />
    </>
  );
};
